import { emailBaseExtensions } from "../editor/marks";
import { UnsubscribeFooterDecoration } from "../editor/unsubscribe-footer-decoration";
import { Extension } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import { blockquoteBlock } from "./blockquote/definition";
import { buttonBlock } from "./button/definition";
import { createNodeExtension } from "./create-extension";
import { dividerBlock } from "./divider/definition";
import { headingBlock } from "./heading/definition";
import {
  htmlBlockBlock,
  htmlRawBlock,
  htmlTextBlock,
} from "./html-fallback/definition";
import { imageBlock } from "./image/definition";
import { listBlockMeta } from "./list/definition";
import { paragraphBlock } from "./paragraph/definition";
import { quoteBlock } from "./quote/definition";
import { EmailCell } from "./row/cell-extension";
import { EmailRow } from "./row/row-extension";
import { EmailBlockDefinition } from "./types";

/**
 * Registry of all standard email blocks (excludes lists which use
 * native Tiptap extensions). Maps block name → EmailBlockDefinition.
 *
 * Uses EmailBlockDefinition<any> because each block has its own specific
 * attrs type, and the generic is contravariant on tiptapComponent props.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const blockRegistry: Record<string, EmailBlockDefinition<any>> = {
  emailParagraph: paragraphBlock,
  emailHeading: headingBlock,
  emailBlockquote: blockquoteBlock,
  emailImage: imageBlock,
  emailButton: buttonBlock,
};

/** All standard blocks as an array (for iteration / block picker) */
export const allBlocks = Object.values(blockRegistry);

/** Blocks grouped by category for the block picker UI */
export const blocksByCategory = [...allBlocks, listBlockMeta].reduce(
  (acc, block) => {
    const cat = block.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(block);
    return acc;
  },
  {} as Record<
    string,
    Array<(typeof allBlocks)[number] | typeof listBlockMeta>
  >,
);

/**
 * Placeholder that works with ReactNodeViewRenderer paragraphs.
 * Uses a widget decoration (inserted into the PM-managed content area)
 * instead of node decorations (which don't propagate to NodeView DOM).
 */
const placeholderPluginKey = new PluginKey<{ initialDoc: Node }>(
  "email-placeholder",
);

const EmailPlaceholder = Extension.create<{ placeholder: string }>({
  name: "emailPlaceholder",
  addOptions() {
    return { placeholder: "" };
  },
  addProseMirrorPlugins() {
    const text = this.options.placeholder;
    return [
      new Plugin({
        key: placeholderPluginKey,
        state: {
          init(_, state) {
            return { initialDoc: state.doc };
          },
          apply(_, value) {
            return value;
          },
        },
        props: {
          decorations(state) {
            // Navigate: doc > emailRow > emailCell > emailParagraph
            const firstRow = state.doc.firstChild;
            if (!firstRow || firstRow.type.name !== "emailRow") {
              return DecorationSet.empty;
            }
            const firstCell = firstRow.firstChild;
            if (!firstCell || firstCell.type.name !== "emailCell") {
              return DecorationSet.empty;
            }
            const firstBlock = firstCell.firstChild;
            if (
              !firstBlock ||
              firstBlock.type.name !== "emailParagraph" ||
              firstBlock.content.size > 0
            ) {
              return DecorationSet.empty;
            }

            // Show placeholder only when the doc hasn't changed from initial
            // (handles signature content: doc has multiple children but user
            // hasn't typed anything yet)
            const { initialDoc } = placeholderPluginKey.getState(state)!;
            if (!state.doc.eq(initialDoc)) {
              return DecorationSet.empty;
            }

            // Position: doc(1) + row(1) + cell(1) = 3 offset to the paragraph's content
            const widget = Decoration.widget(
              3,
              () => {
                const el = document.createElement("span");
                el.textContent = text;
                el.style.cssText =
                  "display:block;height:0;overflow:visible;color:rgb(var(--foreground-muted));pointer-events:none;";
                return el;
              },
              { side: -1, key: "placeholder" },
            );
            return DecorationSet.create(state.doc, [widget]);
          },
        },
      }),
    ];
  },
});

let htmlParseExtensions: ReturnType<typeof createEmailExtensions> | null = null;

/**
 * The extension list used to parse HTML into JSON via `generateJSON`.
 *
 * Built once and reused. Parsing needs no per-editor options, and the list is
 * not cheap to assemble — `generateJSON` derives a full schema from it on every
 * call, and a settings grid parses one template body per card, so rebuilding it
 * each time is pure repeated work.
 */
export function getHtmlParseExtensions() {
  htmlParseExtensions ??= createEmailExtensions();
  return htmlParseExtensions;
}

/**
 * Creates the complete set of Tiptap extensions for the email editor.
 * Combines base extensions (structural + marks) with block node extensions.
 */
export function createEmailExtensions(options?: {
  placeholder?: string;
  initialIsMarketing?: boolean;
  draggable?: boolean;
  mode?: "simple" | "block";
}) {
  const draggable = options?.draggable ?? false;
  const mode = options?.mode ?? "simple";
  const blockExtensions = Object.values(blockRegistry).map((block) =>
    createNodeExtension(block, { draggable, mode }),
  );
  const quoteExtension = createNodeExtension(quoteBlock, { draggable, mode });
  const dividerExtension = createNodeExtension(dividerBlock, {
    draggable,
    mode,
  });
  // Registered last, so the typed blocks come first in the schema's node
  // order. That order is what ProseMirror uses to pick a wrapper for loose
  // text, and a paragraph is the right answer everywhere except inside a
  // preserved container — where `htmlBlock`'s own content expression names
  // `htmlText` first.
  const fallbackExtensions = [
    htmlBlockBlock,
    htmlTextBlock,
    htmlRawBlock,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ].map((block: EmailBlockDefinition<any>) =>
    createNodeExtension(block, { draggable, mode }),
  );
  const extensions = [
    ...emailBaseExtensions,
    EmailRow,
    EmailCell,
    ...blockExtensions,
    quoteExtension,
    dividerExtension,
    ...fallbackExtensions,
    UnsubscribeFooterDecoration.configure({
      initialVisible: options?.initialIsMarketing ?? false,
    }),
  ];

  if (options?.placeholder) {
    extensions.push(
      EmailPlaceholder.configure({ placeholder: options.placeholder }),
    );
  }

  return extensions;
}
